import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowRight, ArrowUpRight, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useStripe } from '../../hooks/useStripe';
import { SubscriptionTier, useSubscription } from '../../hooks/useSubscription';

const tierLabels: Record<SubscriptionTier, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Pro',
  enterprise: 'Enterprise'
};

const getFeaturesByTier = (tier: string) => {
  switch (tier) {
    case 'free':
      return [
        'AI Mode',
        'Design Settings',
        'Widget Settings',
        "Integration code",
        'Mini App Gallery',
        'Conversations (15/month)',
        'Statistics'
      ];
    case 'starter':
      return [
        'AI Mode',
        'Design Settings',
        'Widget Settings',
        "Integration code",
        'Mini App Gallery',
        'Conversations (100/month)',
        'Statistics'
      ];
    case 'professional':
      return [
        'AI Mode',
        'Design Settings',
        'Widget Settings',
        "Integration code",
        'Mini App Gallery',
        'Conversations (500/month)',
        'Statistics',
        'Mini App Products',
        'Mini App Calendly',
        'Mini App Services',
        'Email Notifications'
      ];
    case 'enterprise':
      return [
         'AI Mode',
        'Design Settings',
        'Widget Settings',
        "Integration code",
        'Mini App Gallery',
        'Conversations (500/month)',
        'Statistics',
        'Mini App Products',
        'Mini App Calendly',
        'Mini App Services',
        'Email Notifications',
        'CRM Integration',
        'Dedicated support'
      ];
    default:
      return [];
  }
};

export function SubscriptionManager() {
  const { subscription } = useSubscription();
  const { manageSubscription, loading } = useStripe();
  const navigate = useNavigate();
  const { colors } = useDesignColors();

  if (!subscription) return null;

  const isActive = subscription.status === 'active';
  const endDate = subscription.current_period_end 
    ? format(new Date(subscription.current_period_end), 'PPP', { locale: fr })
    : null;

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-white">
              Plan {tierLabels[subscription.tier as SubscriptionTier]}
            </h3>
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-sm ${
              isActive 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {isActive ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Active</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  <span>Inactive</span>
                </>
              )}
            </div>
          </div>
          {endDate && (
            <p className="text-sm text-gray-400 mt-1">
              {subscription.cancel_at_period_end 
                ? `Se termine le ${endDate}`
                : `Next renewal on ${endDate}`
              }
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {subscription.tier !== 'enterprise' && (
            <button
              onClick={handleUpgrade}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-white transition-all"
              style={{
                background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
              }}
            >
              Upgrade to a higher plan
              <ArrowUpRight className="w-4 h-4" />
            </button>
          )}

          {subscription.tier !== 'free' && (
            <button
              onClick={manageSubscription}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-400 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Manage subscription
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Détails de l'abonnement */}
      <div className="space-y-4">
        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">
            Billing status
          </h4>
          <div className="flex items-center gap-2">
            {subscription.cancel_at_period_end ? (
              <div className="text-yellow-400">
                <p>Scheduled cancellation</p>
                <p className="text-sm text-gray-400">
                  Your subscription will remain active until {endDate}
                </p>
              </div>
            ) : isActive ? (
              <div className="text-green-400">
                <p>Active subscription</p>
                <p className="text-sm text-gray-400">
                  Next renewal on {endDate}
                </p>
              </div>
            ) : (
              <div className="text-red-400">
                <p>Inactive subscription</p>
                <p className="text-sm text-gray-400">
                  Some features are limited
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white/5 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-300 mb-2">
            Included features
          </h4>
          <div className="space-y-2">
            {getFeaturesByTier(subscription.tier as SubscriptionTier).map((feature, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}